"use client";

import { useState } from "react";
import { FlowInput } from "../FlowInput";

/**
 * Loose on purpose: enough to catch a typo like a missing @ or domain, without
 * rejecting the valid-but-unusual addresses a strict pattern tends to.
 */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Digits only, so "+852 1234 5678" and "(852) 12345678" both count. */
const MIN_PHONE_DIGITS = 7;

function Field({
  label,
  htmlFor,
  hint,
  children,
}: {
  label: string;
  htmlFor: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex w-full flex-col items-start gap-2">
      <label htmlFor={htmlFor} className="font-inter text-sm font-bold text-[#F3F4F6]">
        {label}
      </label>
      {children}
      {hint && <span className="font-inter text-xs text-[#9CA3AF]">{hint}</span>}
    </div>
  );
}

export function ContactFormStep({
  name,
  email,
  phone,
  onChange,
  onSubmit,
}: {
  name: string;
  email: string;
  phone: string;
  onChange: (patch: { contactName?: string; contactEmail?: string; contactPhone?: string }) => void;
  /** May reject; the message is shown under the button. */
  onSubmit: () => void | Promise<void>;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const digits = phone.replace(/\D/g, "");
  const canSubmit =
    name.trim().length > 0 &&
    EMAIL_PATTERN.test(email.trim()) &&
    digits.length >= MIN_PHONE_DIGITS;

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        if (!canSubmit || submitting) return;
        setSubmitting(true);
        setError(null);
        try {
          await onSubmit();
        } catch (err) {
          setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
          setSubmitting(false);
        }
      }}
      // Vertically centred on phones like the other short steps. 134px is the
      // nav (64) plus StepShell's sticky header (~70), both above this step.
      className="mx-auto flex min-h-[calc(100dvh-134px)] w-full max-w-[792px] flex-col items-start justify-center gap-6 px-4 py-8 sm:min-h-0 sm:justify-start sm:gap-8 sm:px-6 sm:pb-24 sm:pt-12 lg:pt-[93px]"
    >
      <div className="flex w-full flex-col items-start gap-3 sm:gap-4">
        <h1 className="font-suisse-intl-bold text-[26px] leading-tight text-white sm:text-4xl md:text-5xl lg:text-[56px] lg:leading-[71px]">
          Provide your contact details to continue
        </h1>
        <p className="font-suisse-intl-medium text-base leading-relaxed text-white sm:text-lg sm:leading-[28.8px]">
          We&apos;ll use these to confirm your package and guide you through the next steps of your
          registration.
        </p>
      </div>

      <div className="flex w-full flex-col items-start gap-5 sm:gap-6">
        <Field label="Full name" htmlFor="contact-name">
          <FlowInput
            id="contact-name"
            value={name}
            onChange={(contactName) => onChange({ contactName })}
            placeholder="Your full name"
            autoComplete="name"
            required
          />
        </Field>

        <Field label="Email address" htmlFor="contact-email">
          <FlowInput
            id="contact-email"
            type="email"
            value={email}
            onChange={(contactEmail) => onChange({ contactEmail })}
            placeholder="you@company.com"
            autoComplete="email"
            inputMode="email"
            required
          />
        </Field>

        <Field
          label="Contact number"
          htmlFor="contact-phone"
          hint="Include your country code, e.g. +852 for Hong Kong."
        >
          <FlowInput
            id="contact-phone"
            type="tel"
            value={phone}
            onChange={(contactPhone) => onChange({ contactPhone })}
            placeholder="+852 1234 5678"
            autoComplete="tel"
            inputMode="tel"
            required
          />
        </Field>
      </div>

      <button
        type="submit"
        disabled={!canSubmit || submitting}
        className="flex h-14 w-full items-center justify-center rounded-full bg-white px-6 font-suisse-intl text-xl leading-5 text-[#2A4775] transition-opacity hover:opacity-90 disabled:pointer-events-none disabled:opacity-50 sm:max-w-[280px]"
      >
        {submitting ? "Submitting…" : "Submit"}
      </button>

      {error && (
        <p role="alert" className="font-inter text-sm text-[#FF585B]">
          {error}
        </p>
      )}

      <p className="font-inter text-xs text-[#9CA3AF]">
        Next, you&apos;ll be taken to a secure checkout to pay. Once your payment is confirmed, we&apos;ll email you your MuteTaxes dashboard login.
      </p>
    </form>
  );
}
